import LabelManager from './LabelManager';
import StyleManager from './StyleManager';


/**
 * MapStateManager - Hanterar all map-relaterad state
 * 
 * ANSVAR:
 * - Map-instanser (map, maps)
 * - Selected areas som GeoJSON features (this.selectedAreas)
 * - Turf polygons för beräkningar
 * - Selection type och history
 * - Map styling och labels
 * 
 * INTE ANSVAR FÖR:
 * - React component state
 * - Units data
 * - District data för att spara
 * - UI state (loading, modals, etc.)
 */
class MapStateManager {
    constructor() {
        this.map = null;
        this.maps = null;
        this.type = null;
        this.turfPolygons = {};
        this.history = [];
        this.districtTypes = [];
        this.selectedAreas = []; // Array av GeoJSON features
        this.previousSelectedAreaIds = new Set(); // För att spåra tidigare valda areas för optimering
        this.disableFitBounds = false;
        this.tools = null;
        this.popup = null;
        this.isSalesDistricts = false;
        this.salesDistricts = null;
        this.currentDistrictId = null; // ID för det nuvarande distriktet (för framtida ID-matchning)
        this.currentDistrictName = null; // Name för det nuvarande distriktet (används för matchning tills API:et stödjer id)
        this.selectionType = 'clear';
        this.labelManager = new LabelManager();
        this.styleManager = new StyleManager();
        this.onAreaClick = null;
        this.styleUpdateFrame = null; // För requestAnimationFrame i applyStyles
        
        // Callbacks för att synkronisera state-ändringar
        this.onSelectedAreasChanged = null;
    }

    /**
     * Initialize map when Google Maps API is loaded
     * @param {Object} params - { map, maps }
     * @throws {Error} If map or maps is null
     */
    async handleApiLoaded({ map, maps }) {
        if (!map || !maps) {
            const error = new Error('MapStateManager: Map or maps is null in handleApiLoaded');
            console.error(error.message);
            throw error;
        }

        // Store the map and maps instances
        this.map = map;
        this.maps = maps;

        // Add event listeners for zoom and pan
        this.map.addListener('zoom_changed', () => {
            this.labelManager.removeAllLabels();
        });

        this.map.addListener('idle', () => {
            this.labelManager.removeAllLabels();
            
            if (this.selectionType !== 'clear') {
                this.labelManager.handleLabels({
                    map: this.map,
                    maps: this.maps,
                    turfPolygons: this.turfPolygons,
                    selectionType: this.selectionType,
                    isSalesDistricts: this.isSalesDistricts,
                    salesDistricts: this.salesDistricts
                });
            }
        });

        // Initialize the map data layer with default style
        this.styleManager.setDefaultStyle(this.map);

        // Add click event listener for areas
        this.styleManager.attachAreaClickEvent(
            this.map,
            this.maps,
            (featureId) => {
                if (this.selectionType !== 'clear' && this.turfPolygons[this.selectionType]) {
                    const data = this.turfPolygons[this.selectionType].find(data => data.feature.properties.id === featureId);
                    if (data) {
                        // Emit an event that the area was clicked
                        if (this.onAreaClick) {
                            this.onAreaClick(data.feature);
                        }
                    }
                }
            }
        );

        // Force initial style update
        this.applyStyles();
    }

    getMap() {
        return this.map;
    }

    getMaps() {
        return this.maps;
    }

    addTools(tools) {
        this.tools = tools;
    }

    setSalesDistricts(districts) {
        this.salesDistricts = districts;
        this.labelManager.handleLabels({
            map: this.map,
            maps: this.maps,
            turfPolygons: this.turfPolygons,
            selectionType: this.selectionType,
            isSalesDistricts: this.isSalesDistricts,
            salesDistricts: this.salesDistricts
        });
    }

    setSalesDistrictsVisible(isVisible) {
        this.isSalesDistricts = isVisible;
        this.labelManager.handleLabels({
            map: this.map,
            maps: this.maps,
            turfPolygons: this.turfPolygons,
            selectionType: this.selectionType,
            isSalesDistricts: this.isSalesDistricts,
            salesDistricts: this.salesDistricts
        });
    }

    /**
     * Sätt ID och name för det nuvarande distriktet som ska exkluderas från sales district styling
     * @param {string|null} districtId - District ID (för framtida ID-matchning)
     * @param {string|null} districtName - District name (används för matchning tills API:et stödjer id)
     */
    setCurrentDistrictId(districtId, districtName = null) {
        this.currentDistrictId = districtId;
        this.currentDistrictName = districtName;
        // Uppdatera styles när currentDistrictId ändras
        this.applyStyles();
    }

    setPopup(popup) {
        this.popup = popup;
    }

    setDisableFitBounds(disable) {
        this.disableFitBounds = disable;
    }

    /**
     * Set selected areas and notify listeners
     * @param {Array} selectedAreas - Array of GeoJSON features
     * @param {boolean} addToHistory - Whether to add to history
     * @param {boolean} isLockedHistoryEntry - Whether this is a locked history entry
     */
    setSelectedAreas(selectedAreas, addToHistory = true, isLockedHistoryEntry = false) {
        if (!selectedAreas) {
            this.selectedAreas = [];
            this.applyStyles();
            // Notify listeners of change
            if (this.onSelectedAreasChanged) {
                this.onSelectedAreasChanged(this.selectedAreas);
            }
            return;
        }

        // Filter out any undefined or invalid areas
        this.selectedAreas = selectedAreas.filter(area => area && area.properties && area.properties.id);
        
        // If we have selected areas but no selection type, set it based on the first area's type
        if (this.selectedAreas.length > 0 && this.selectionType === 'clear') {
            const firstArea = this.selectedAreas[0];
            if (firstArea && firstArea.properties && firstArea.properties.type) {
                this.selectionType = firstArea.properties.type;
            }
        }

        //Add current selected areas to history
        if (addToHistory) {
            this.setHistory({isLockedHistoryEntry, selectedAreas: this.selectedAreas});    
        }
        
        // Force style update
        this.applyStyles();
        
        // Notify listeners of change
        if (this.onSelectedAreasChanged) {
            this.onSelectedAreasChanged(this.selectedAreas);
        }
    }

    /**
     * Get selected areas (array of GeoJSON features)
     * @returns {Array} Array of GeoJSON features
     */
    getSelectedAreas() {
        return this.selectedAreas;
    }

    /**
     * Set callback to be called when selected areas change
     * @param {Function} callback - Function that receives the new selectedAreas array
     */
    setOnSelectedAreasChanged(callback) {
        this.onSelectedAreasChanged = callback;
    }

    getSelectionType() {
        return this.selectionType;
    }

    setSelectionType(type) {
        this.selectionType = type;
        
        // Återställ previousSelectedAreaIds när selection type ändras
        // så att alla features uppdateras korrekt
        this.previousSelectedAreaIds = new Set();
        
        // Force style update
        this.applyStyles();
    }

    setTurfPolygons(turfPolygons) {
        this.turfPolygons = turfPolygons;
    }

    /**
     * Apply styles to map features based on selected areas
     * Safe to call even if map is not initialized (will silently return)
     */
    applyStyles() {
        if (!this.isInitialized() || !this.map.data) {
            return;
        }
        
        // Använd requestAnimationFrame för att batcha style-uppdateringar
        // Detta förbättrar prestandan när många distrikt väljs i snabb följd
        if (this.styleUpdateFrame) {
            cancelAnimationFrame(this.styleUpdateFrame);
        }
        
        this.styleUpdateFrame = requestAnimationFrame(() => {
            // Skapa Set för nuvarande valda areas
            const currentSelectedAreaIds = new Set();
            if (this.selectedAreas && this.selectedAreas.length > 0) {
                this.selectedAreas.forEach(area => {
                    if (area && area.properties && area.properties.id) {
                        currentSelectedAreaIds.add(area.properties.id);
                    }
                });
            }

            // Hitta features som faktiskt ändrats (valt eller avvalt)
            const changedFeatureIds = new Set();
            
            // Features som blev valda (finns i current men inte i previous)
            currentSelectedAreaIds.forEach(id => {
                if (!this.previousSelectedAreaIds.has(id)) {
                    changedFeatureIds.add(id);
                }
            });
            
            // Features som blev avvalda (finns i previous men inte i current)
            this.previousSelectedAreaIds.forEach(id => {
                if (!currentSelectedAreaIds.has(id)) {
                    changedFeatureIds.add(id);
                }
            });

            // Om inga features ändrats, hoppa över style-uppdatering
            if (changedFeatureIds.size === 0 && this.previousSelectedAreaIds.size === currentSelectedAreaIds.size) {
                this.styleUpdateFrame = null;
                return;
            }

            // För första gången eller om selection type ändrats, uppdatera alla
            if (this.previousSelectedAreaIds.size === 0 || changedFeatureIds.size > 50) {
                // För många ändringar, uppdatera alla (mer effektivt än individuella uppdateringar)
                this.styleManager.setDefaultStyle(this.map);
                this.styleManager.applyStyles({
                    map: this.map,
                    selectedAreas: this.selectedAreas,
                    selectionType: this.selectionType,
                    currentDistrictId: this.currentDistrictId,
                    currentDistrictName: this.currentDistrictName
                });
            } else {
                // Bara uppdatera features som faktiskt ändrats
                this.styleManager.applyStylesSelective({
                    map: this.map,
                    selectedAreas: this.selectedAreas,
                    selectionType: this.selectionType,
                    changedFeatureIds: changedFeatureIds,
                    currentSelectedAreaIds: currentSelectedAreaIds,
                    previousSelectedAreaIds: this.previousSelectedAreaIds,
                    currentDistrictId: this.currentDistrictId,
                    currentDistrictName: this.currentDistrictName
                });
            }

            // Uppdatera previous selected areas
            this.previousSelectedAreaIds = new Set(currentSelectedAreaIds);
            
            this.styleUpdateFrame = null;
        });
    }

    setOnAreaClick(callback) {
        this.onAreaClick = callback;
    }

    setDistrictTypes(districtType) {
        this.districtTypes.push(districtType);
    }

    getDistrictTypes() {
        return this.districtTypes;
    }

    undo() {
        if (this.history.length > 0) {
            if (!this.history[this.history.length - 1].isLockedHistoryEntry) {
                this.history.pop(); // Remove current state
                const previousState = this.history[this.history.length - 1];
                // setSelectedAreas will notify listeners via callback
                this.setSelectedAreas(previousState.selectedAreas, false);

                if (this.history[this.history.length - 1].isLockedHistoryEntry) {
                    return false;
                }

                return this.selectedAreas.length > 0;
            } else {
                return false;
            }
            
        }
        return false;
    }

    getHistory() {
        return this.history;
    }

    setHistory(history) {
        this.history.push(history);
    }

    /**
     * Zoom map to fit selected areas or specific district IDs
     * @param {Array|null} districtIds - Optional array of district IDs to zoom to. If null, zooms to all selected areas
     * @throws {Error} If map is not initialized
     */
    zoomToDistrict(districtIds = null) {
        if (!this.isInitialized()) {
            const error = new Error('MapStateManager: Maps not initialized. Call handleApiLoaded first.');
            console.warn(error.message);
            throw error;
        }

        const bounds = new this.maps.LatLngBounds();
    
        this.map.data.forEach((shape) => {
            if (districtIds === null || (districtIds && districtIds.indexOf(shape.getProperty('id')) > -1)) {            
                if (shape.getGeometry().getType() === 'GeometryCollection') {
                    shape.getGeometry().getArray().forEach(inner => {
                        inner.forEachLatLng(latlng => {
                            bounds.extend(latlng);
                        });                    
                    });
                } else {
                    shape.getGeometry().forEachLatLng((latlng) => {
                        bounds.extend(latlng);
                    });
                }
            }
        });
    
        this.map.fitBounds(bounds);
    }

    /**
     * Check if map is initialized
     * @returns {boolean} True if map and maps are initialized
     */
    isInitialized() {
        return this.map !== null && this.maps !== null;
    }

    /**
     * Get turf polygons for a specific type
     * @param {string} type - Layer type
     * @returns {Array|null} Turf polygons for the type, or null if not found
     */
    getTurfPolygons(type) {
        return this.turfPolygons[type] || null;
    }

    /**
     * Check if a layer type is loaded
     * @param {string} type - Layer type
     * @returns {boolean} True if layer is loaded
     */
    hasLayerType(type) {
        return this.turfPolygons[type] !== undefined;
    }

    /**
     * Get all turf polygons
     * @returns {Object} Object with layer types as keys and turf polygons as values
     */
    getAllTurfPolygons() {
        return this.turfPolygons;
    }

    /**
     * Update turf polygons (merge with existing)
     * @param {Object} newTurfPolygons - Object with layer types as keys
     */
    updateTurfPolygons(newTurfPolygons) {
        this.turfPolygons = {
            ...this.turfPolygons,
            ...newTurfPolygons
        };
    }
}

export default MapStateManager; 